import React from 'react';
import type { Language } from '@nordcrest/shared';
import { t } from '../../i18n';
import { Button, Card, CardContent } from '../ui';

interface IntroScreenProps {
  language: Language;
  onStart: () => void;
  onLanguageToggle: () => void;
  isLoading: boolean;
}

export function IntroScreen({ language, onStart, onLanguageToggle, isLoading }: IntroScreenProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Nordcrest</h1>
          <p className="text-gray-500 mt-1">AI Consulting</p>
        </div>

        <Card>
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              {t('survey.intro.title', language)}
            </h2>

            <p className="text-gray-600 text-center mb-8">
              {t('survey.intro.description', language)}
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-700 font-semibold text-sm">1</span>
                </div>
                <div className="ml-4">
                  <p className="font-medium text-gray-900">
                    {language === 'sv' ? 'Bakgrundsfrågor' : 'Background questions'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {language === 'sv' ? 'Om dig och ditt företag' : 'About you and your company'}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-700 font-semibold text-sm">2</span>
                </div>
                <div className="ml-4">
                  <p className="font-medium text-gray-900">
                    {language === 'sv' ? 'AI-mognadsbedömning' : 'AI maturity assessment'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {language === 'sv' ? 'Bedöm er nuvarande situation' : 'Assess your current situation'}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-700 font-semibold text-sm">3</span>
                </div>
                <div className="ml-4">
                  <p className="font-medium text-gray-900">
                    {language === 'sv' ? 'Personlig rapport' : 'Personal report'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {language === 'sv' ? 'Få insikter och rekommendationer' : 'Get insights and recommendations'}
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={onStart}
              isLoading={isLoading}
              className="w-full"
              size="lg"
            >
              {t('survey.intro.start', language)}
            </Button>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <button
            onClick={onLanguageToggle}
            className="text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            {t('language.switchTo', language)}
          </button>
        </div>
      </div>
    </div>
  );
}
